<template>
  <v-dialog v-model="dialog" width="700" content-class="service-extension-dialog" :persistent="true">
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      top
      :color="snackbar.color"
      rounded
      centered
    >
      {{ snackbar.text }}
    </v-snackbar>
    <v-card class="dialog-card">
      <v-card-title tag="p" class="service-extension-dialog-title text-break mb-0 mt-0 text-center justify-center">
        <v-row>
          <v-col cols="10" offset="1">
            {{ $t('ServiceExtensionDialog.title') }}
          </v-col>
          <v-col cols="1" class="px-0 text-end" align-self="start">
            <v-btn small icon @click="close" class="my-0">
              <v-icon color="#000000">close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <template v-if="step === steps.queryServiceExtension && pendingServiceExtensions && pendingServiceExtensions.length > 0">
        <v-card-text>
          <v-row justify="center" class="my-2">
            <p class="text-center service-extension-dialog-hint mb-0">
              <v-icon class="service-extension-icon" size="1rem">mdi-clock-outline</v-icon>{{$t('ServiceExtensionDialog.serviceEndsOn', { deviceName: device.campaign.entityName, date: formatEndDate(device.serviceEndDate)})}}
            </p>
          </v-row>
          <v-row justify="center" class="mt-2">
            <p class="text-center service-extension-dialog-pending-title">{{ $t('ServiceExtensionDialog.pendingServiceExtensionTitle') }}</p>
          </v-row>
          <v-row justify="center" class="mt-2">
            <p class="text-center service-extension-dialog-pending-detail">{{ $t('ServiceExtensionDialog.pendingServiceExtensionDetail', { date: formatEndDate(pendingServiceExtensions[0].newServiceEndDate), userName: getUserName(pendingServiceExtensions[0].createdBy.id, pendingServiceExtensions[0].createdBy.name) }) }}</p>
          </v-row>
          <v-row justify="center" class="mt-2">
            <p class="text-center service-extension-dialog-pending-description">{{ $t('ServiceExtensionDialog.pendingServiceExtensionDescription') }}</p>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-center mb-5">
          <v-btn
            depressed
            color="#0098E1"
            class="submit-button"
            @click="accept()"
            :loading="loading"
          >
            {{ $t('ServiceExtensionDialog.accept') }}
          </v-btn>
        </v-card-actions>
      </template>
      <v-form v-else-if="step === steps.queryServiceExtension" ref="form" v-model="isFormValid">
        <v-card-text>
          <v-row justify="center" class="my-2">
            <p class="text-center service-extension-dialog-hint mb-0">
              <v-icon class="service-extension-icon" size="1rem">mdi-clock-outline</v-icon>{{$t('ServiceExtensionDialog.serviceEndsOn', { deviceName: device.campaign.entityName, date: formatEndDate(device.serviceEndDate)})}}
            </p>
          </v-row>
          <v-row justify="center" class="mt-2">
            <p class="text-center service-extension-dialog-subtitle">{{ $t('ServiceExtensionDialog.queryNewServiceEndDate') }}</p>
          </v-row>
          <v-row justify="center" class="mt-2">
            <v-col cols="8" md="6">
              <v-menu
                v-model="serviceEndDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="newServiceEndDate"
                    :label="`${$t('ServiceExtensionDialog.serviceEndDate')}`"
                    :title="$t('ServiceExtensionDialog.serviceEndDate')"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    persistent-hint
                    flat
                    color="#0098E1"
                    clearable
                    prepend-icon="mdi-calendar"
                    :rules="[rules.fieldRequired, rules.validateNewServiceEndDate]"
                    hide-details="auto"
                    class="pt-0"
                  />
                </template>
                <v-date-picker
                  v-model="auxNewServiceEndDate"
                  @change="formatDate($event)"
                  color="#0098E1"
                  locale="es-AR"
                  :min="getCurrentDate()"
                />
              </v-menu>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-center mb-5">
          <v-btn
            depressed
            color="#0098E1"
            class="submit-button"
            @click="submit()"
            :loading="loading"
          >
            {{ $t('ServiceExtensionDialog.submit') }}
          </v-btn>
        </v-card-actions>
      </v-form>
      <template v-else-if="step === steps.confirmServiceExtension">
        <v-card-text>
          <div class="text-center mt-2 mb-2">
            <p class="service-extension-dialog-confirm-subtitle mb-0 d-inline-flex align-center">
              <v-img :src="require('@/assets/icons/check.png')" max-width="1.15rem" max-height="1.15rem" class="mr-2" />
              {{$t('ServiceExtensionDialog.serviceExtensionReceived')}}
            </p>
          </div>
          <v-row justify="center" class="mt-2">
            <p class="text-center service-extension-dialog-confirm-text">{{ $t('ServiceExtensionDialog.serviceExtensionReceivedSubtitle') }}</p>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-center mb-5">
          <v-btn
            depressed
            color="#0098E1"
            class="submit-button"
            @click="accept()"
            :loading="loading"
          >
            {{ $t('ServiceExtensionDialog.accept') }}
          </v-btn>
        </v-card-actions>
      </template>
      <template v-else-if="step === steps.dateWithinPeriod">
        <v-card-text>
          <div class="text-center mt-2 mb-2">
            <p class="service-extension-dialog-confirm-subtitle mb-0 d-inline-flex align-center">
              {{$t('ServiceExtensionDialog.dateWithinPeriod')}}
            </p>
          </div>
          <v-row justify="center" class="mt-4">
            <p class="text-center service-extension-dialog-within-period-description">{{ $t('ServiceExtensionDialog.dateWithinPeriodSubtitle') }}</p>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-center mb-5">
          <v-btn
            depressed
            color="#0098E1"
            class="submit-button"
            @click="accept()"
            :loading="loading"
          >
            {{ $t('ServiceExtensionDialog.accept') }}
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { isoDateToSlashes, ddMMyyyyToJsDate } from '../../lib/helpers'
import { WEBHOOKS_PATHS, EVENTS_TO_TRACK, PRODUCT_TYPES } from '../../constants/const'
import { auth } from '../../plugins/firebase'
import { trackEvent } from '../../lib/track-events'

export default {
  props: {
    dialog: {
      type: Boolean,
      required: true
    },
    device: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      serviceEndDateMenu: false,
      auxNewServiceEndDate: '',
      newServiceEndDate: '',
      isFormValid: false,
      rules: {
        fieldRequired: value => !!value || 'Este campo es requerido',
        validateNewServiceEndDate: value => !value || this.validateNewServiceEndDate(value)
      },
      step: 'queryServiceExtension',
      steps: {
        queryServiceExtension: 'queryServiceExtension',
        confirmServiceExtension: 'confirmServiceExtension',
        dateWithinPeriod: 'dateWithinPeriod'
      },
      snackbar: {
        show: false,
        text: '',
        color: '',
        timeout: 3000
      },
    }
  },
  methods: {
    close() {
      trackEvent(EVENTS_TO_TRACK.CLOSE_SERVICE_EXTENSION_DIALOG, { deviceId: this.device.id })
      this.$emit('update:dialog', false)
    },
    getCurrentDate() {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    formatDate(val) {
      const [year, month, day] = val.split('-')
      this.newServiceEndDate = `${day}/${month}/${year}`
    },
    formatEndDate (val) {
      if (val instanceof Date) {
        return isoDateToSlashes(val.toISOString())
      } else if (val.toDate) {
        return isoDateToSlashes(val.toDate().toISOString())
      }
      return isoDateToSlashes(val)
    },
    validateNewServiceEndDate(value) {
      const newEndDate = ddMMyyyyToJsDate(value)
      if (newEndDate <= this.device.serviceEndDate) {
        return this.$t('Config.newServiceEndDateRule')
      }
      return true
    },
    async submit() {
      trackEvent(EVENTS_TO_TRACK.REQUEST_SERVICE_EXTENSION, { deviceId: this.device.id })
      this.$refs.form.validate()
      if (this.isFormValid) {
        try {
          this.loading = true

          // If date is within period, show dateWithinPeriod step instead of sending request
          const isDateWithinPeriod = await this.checkIfDateWithinPeriod(this.newServiceEndDate)
          if (isDateWithinPeriod) {
            this.step = this.steps.dateWithinPeriod
            return
          }

          const response = await fetch(`${process.env.WEBHOOKS_URL}${WEBHOOKS_PATHS.SERVICE_EXTENSION}`, {
            method: 'POST',
            body: JSON.stringify({
              deviceId: this.device.id,
              newServiceEndDate: ddMMyyyyToJsDate(this.newServiceEndDate),
              createdBy: {
                id: this.$store.getters.user.id,
                name: `${this.$store.getters.user.name} ${this.$store.getters.user.lastName}`
              }
            }),
            headers: {
              'auth-token': await auth.currentUser.getIdToken(),
              authorization: process.env.WEBHOOKS_BASIC_TOKEN,
              'Content-Type': 'application/json'
            }
          })

          if (response.ok) {
            this.step = this.steps.confirmServiceExtension
            const campaign = this.$store.getters.getCampaign(this.$store.getters.activeCampaign)
            const deviceIndex = campaign.devices.findIndex(device => device.id === this.device.id)
            campaign.devices[deviceIndex].serviceExtensionRequests.push({
              newServiceEndDate: ddMMyyyyToJsDate(this.newServiceEndDate),
              status: 'pending',
              createdBy: {
                id: this.$store.getters.user.id,
                name: `${this.$store.getters.user.name} ${this.$store.getters.user.lastName}`
              },
              createdAt: new Date()
            })
            this.$store.commit('updateCampaign', {
              campaignId: this.device.campaign.id,
              campaignData: campaign
            })
            this.$store.commit('setDevices', campaign.devices)
          } else {
            this.snackbar = {
              ...this.snackbar,
              show: true,
              text: this.$t('ServiceExtensionDialog.serviceExtensionError'),
              color: 'error'
            }
          }
        } catch (error) {
          this.snackbar = {
            ...this.snackbar,
            show: true,
            text: this.$t('ServiceExtensionDialog.serviceExtensionError'),
            color: 'error'
          }
        } finally {
          this.loading = false
        }
      }
    },
    accept () {
      trackEvent(EVENTS_TO_TRACK.CLICK_ACCEPT_SERVICE_EXTENSION, { deviceId: this.device.id })
      this.close()
    },
    getUserName (userId, fallBackName) {
      const user = this.$store.getters.getUserById(userId)
      return user ? `${user.name} ${user.lastName}` : fallBackName
    },
    async checkIfDateWithinPeriod(selectedDate) {
      try {
        // Get the campaign data - if it's a Firestore reference, get the actual data
        const campaign = this.$store.getters.getCampaign(this.$store.getters.activeCampaign)

        if (!campaign || !campaign.preDataProductsDetail) {
          return false
        }

        // Find the monitoring product for this device type
        const monitoringProduct = campaign.preDataProductsDetail.find(product =>
          product.type === PRODUCT_TYPES.MONITORING &&
          product.devices.find(device => device.instanceId === this.device.id)
        )

        if (!monitoringProduct) {
          return false
        }

        // Find the device in the monitoring product
        const deviceServiceData = monitoringProduct.devices.find(device =>
          device.instanceId === this.device.id
        )

        if (!deviceServiceData) {
          return false
        }

        if (!deviceServiceData.startDate) {
          return false
        }

        // Calculate base service end date (device service start date + duration months)
        const startDate = ddMMyyyyToJsDate(deviceServiceData.startDate)
        const baseEndDate = new Date(startDate)
        baseEndDate.setMonth(baseEndDate.getMonth() + monitoringProduct.durationMonths)

        // Find extensions for this device
        const extensionProducts = campaign.preDataProductsDetail.filter(product =>
          product.type === PRODUCT_TYPES.EXTENSION
        )

        let totalExtensionMonths = 0
        extensionProducts.forEach(product => {
          const deviceExtension = product.extensions.find(ext =>
            ext.device === this.device.id && ext.applied
          )
          if (deviceExtension) {
            totalExtensionMonths += deviceExtension.unitsToExtend * product.durationMonths
          }
        })

        // Calculate final service end date (base + extensions)
        const finalEndDate = new Date(baseEndDate)
        finalEndDate.setMonth(finalEndDate.getMonth() + totalExtensionMonths)

        // Check if selected date is within the service period
        const selectedDateObj = ddMMyyyyToJsDate(selectedDate)

        return selectedDateObj <= finalEndDate && selectedDateObj >= startDate
      } catch (error) {
        console.error('Error checking if date is within period:', error)
        return false
      }
    }
  },
  computed: {
    pendingServiceExtensions () {
      return this.device.serviceExtensionRequests.filter(extension => extension.status === 'pending')
    }
  },
  watch: {
    dialog(newVal) {
      this.$emit('update:dialog', newVal)
    }
  }
}
</script>

<style scoped>
.dialog-card {
  max-height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dialog-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.scrollable-content {
  padding-top: 10px;
  flex: 1;
  overflow-y: auto;
  max-height: 50vh;
  padding-right: 8px;

  .v-label {
    font-size: 20px;
  }
}

.scrollable-content::-webkit-scrollbar {
  width: 6px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.service-extension-icon {
  display: inline-block;
  vertical-align: baseline;
  margin-right: 0.25rem;
  position: relative;
  top: 0.1em;
}
</style>



// WEBPACK FOOTER //
// ServiceExtensionDialog.vue?1606feee